import { create } from "zustand";
import type { User } from "../types";
import { auth } from "../api/endpoints";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loadFromStorage: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isAuthLoading: true, // Start loading — will resolve via /me cookie check

  login: async (email: string, password: string) => {
    // Login sets HttpOnly cookies (access_token, refresh_token, csrf_token)
    const loginResponse = await auth.login(email, password);
    set({ isAuthenticated: true, isAuthLoading: false, user: loginResponse.user });
  },

  logout: async () => {
    try {
      await auth.logout();
    } catch {
      // Best-effort — clear local state regardless
    }
    set({ user: null, isAuthenticated: false, isAuthLoading: false });
  },

  loadFromStorage: async () => {
    // Validate session via HttpOnly cookie (sent automatically with withCredentials)
    set({ isAuthLoading: true });
    try {
      const user = await auth.getMe();
      set({ user, isAuthenticated: true, isAuthLoading: false });
    } catch {
      set({ user: null, isAuthenticated: false, isAuthLoading: false });
    }
  },

  switchCompany: async (companyId: string) => {
    const response = await auth.switchCompany(companyId);
    set({ isAuthenticated: true, user: response.user });
  },
}));
